(function() {
  'use strict';

  const TRACKER_VERSION = '1.0.0';
  const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
  const STORAGE_PREFIX = 'bnb_';
  
  // Configuration - set by the Liquid snippet
  const config = window.BarknBigConfig || {};
  const API_ENDPOINT = config.apiEndpoint || '';
  const SHOP_DOMAIN = config.shopDomain || '';

  if (!API_ENDPOINT) {
    console.warn('[BarknBig] Tracker not configured - missing API endpoint');
    return;
  }

  // Utility functions
  function generateId() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  function getStorageItem(key) {
    try {
      return localStorage.getItem(STORAGE_PREFIX + key);
    } catch (e) {
      return null;
    }
  }

  function setStorageItem(key, value) {
    try {
      localStorage.setItem(STORAGE_PREFIX + key, value);
    } catch (e) {
      // localStorage not available
    }
  }

  function getDeviceType() {
    const ua = navigator.userAgent;
    if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
    if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
    return 'desktop';
  }

  function getPageType() {
    const path = window.location.pathname;
    if (path === '/' || path === '') return 'home';
    if (path.includes('/collections/') && path.includes('/products/')) return 'product';
    if (path.includes('/products/')) return 'product';
    if (path.includes('/collections/')) return 'collection';
    if (path.includes('/cart')) return 'cart';
    if (path.includes('/checkouts/')) return 'checkout';
    if (path.includes('/thank_you') || path.includes('/orders/')) return 'thank_you';
    if (path.includes('/account')) return 'account';
    if (path.includes('/search')) return 'search';
    return 'other';
  }

  function getProductIdFromPage() {
    // Try to get product ID from Shopify's meta tag or global variable
    if (window.ShopifyAnalytics?.meta?.product?.id) {
      return String(window.ShopifyAnalytics.meta.product.id);
    }
    if (window.meta?.product?.id) {
      return String(window.meta.product.id);
    }
    // Fallback: extract from canonical URL or data attributes
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      const match = canonical.href.match(/\/products\/([^\/\?]+)/);
      if (match) return match[1];
    }
    return null;
  }

  function getCollectionIdFromPage() {
    if (window.ShopifyAnalytics?.meta?.page?.resourceId && getPageType() === 'collection') {
      return String(window.ShopifyAnalytics.meta.page.resourceId);
    }
    return null;
  }

  function getCartToken() {
    // Cart token is stored in a cookie by Shopify
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'cart') return value;
    }
    return null;
  }

  function getCheckoutToken() {
    const path = window.location.pathname;
    const match = path.match(/\/checkouts\/([^\/]+)/);
    return match ? match[1] : null;
  }

  // Visitor management
  function getOrCreateVisitorKey() {
    let visitorKey = getStorageItem('visitor_key');
    if (!visitorKey) {
      visitorKey = generateId();
      setStorageItem('visitor_key', visitorKey);
    }
    return visitorKey;
  }

  // Session management
  function getOrCreateSession() {
    let sessionKey = getStorageItem('session_key');
    let sessionStart = parseInt(getStorageItem('session_start') || '0', 10);
    let lastActivity = parseInt(getStorageItem('last_activity') || '0', 10);
    const now = Date.now();

    // Check if session expired (30 min inactivity)
    if (!sessionKey || (now - lastActivity) > SESSION_TIMEOUT_MS) {
      sessionKey = generateId();
      sessionStart = now;
      setStorageItem('session_key', sessionKey);
      setStorageItem('session_start', String(sessionStart));
    }

    // Update last activity
    setStorageItem('last_activity', String(now));

    return { sessionKey, sessionStart, isNew: sessionStart === now };
  }

  // Event queue for batching
  let eventQueue = [];
  let flushTimeout = null;

  function queueEvent(event) {
    eventQueue.push({
      ...event,
      timestamp: new Date().toISOString()
    });

    // Flush after a short delay to batch events
    if (flushTimeout) clearTimeout(flushTimeout);
    flushTimeout = setTimeout(flushEvents, 500);
  }

  function flushEvents() {
    if (eventQueue.length === 0) return;

    const visitorKey = getOrCreateVisitorKey();
    const { sessionKey } = getOrCreateSession();
    const events = [...eventQueue];
    eventQueue = [];

    const payload = {
      visitorKey,
      sessionKey,
      deviceType: getDeviceType(),
      cartToken: getCartToken(),
      checkoutToken: getCheckoutToken(),
      customerId: config.customerId || null,
      emailHash: config.customerEmail || null,
      events
    };

    // Send to backend
    const jsonPayload = JSON.stringify(payload);
    
    if (navigator.sendBeacon) {
      // Wrap in Blob with correct Content-Type for sendBeacon
      const blob = new Blob([jsonPayload], { type: 'application/json' });
      navigator.sendBeacon(API_ENDPOINT + '/api/analytics/ingest', blob);
    } else {
      fetch(API_ENDPOINT + '/api/analytics/ingest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: jsonPayload,
        keepalive: true
      }).catch(function(err) {
        console.warn('[BarknBig] Failed to send events:', err);
      });
    }
  }

  // Track page view
  function trackPageView() {
    const pageType = getPageType();
    let eventType = 'page_viewed';

    if (pageType === 'product') eventType = 'product_viewed';
    else if (pageType === 'collection') eventType = 'collection_viewed';
    else if (pageType === 'cart') eventType = 'cart_viewed';
    else if (pageType === 'checkout') eventType = 'checkout_started';
    else if (pageType === 'thank_you') eventType = 'checkout_completed';

    queueEvent({
      eventType,
      urlPath: window.location.pathname,
      urlFull: window.location.href,
      referrer: document.referrer || null,
      pageTitle: document.title,
      productId: getProductIdFromPage(),
      collectionId: getCollectionIdFromPage(),
      cartToken: getCartToken(),
      checkoutToken: getCheckoutToken(),
      metadata: {
        pageType,
        shopDomain: SHOP_DOMAIN,
        trackerVersion: TRACKER_VERSION
      }
    });
  }

  // Track add to cart
  function trackAddToCart(productId, variantId, quantity, price) {
    queueEvent({
      eventType: 'product_added_to_cart',
      urlPath: window.location.pathname,
      urlFull: window.location.href,
      productId: String(productId),
      cartToken: getCartToken(),
      metadata: {
        variantId: String(variantId),
        quantity,
        price,
        shopDomain: SHOP_DOMAIN
      }
    });
  }

  // Hook into Shopify's add to cart
  function interceptAddToCart() {
    // Intercept form submissions
    document.addEventListener('submit', function(e) {
      const form = e.target;
      if (form.action && form.action.includes('/cart/add')) {
        const productId = form.querySelector('[name="id"]')?.value;
        const quantity = form.querySelector('[name="quantity"]')?.value || 1;
        if (productId) {
          trackAddToCart(productId, productId, parseInt(quantity, 10), null);
        }
      }
    }, true);

    // Intercept fetch/XHR for AJAX add to cart
    const originalFetch = window.fetch;
    window.fetch = function(url, options) {
      const result = originalFetch.apply(this, arguments);
      
      if (typeof url === 'string' && url.includes('/cart/add')) {
        result.then(function(response) {
          if (response.ok && options?.body) {
            try {
              let data;
              if (typeof options.body === 'string') {
                data = JSON.parse(options.body);
              } else if (options.body instanceof FormData) {
                data = { id: options.body.get('id'), quantity: options.body.get('quantity') };
              }
              if (data?.id) {
                trackAddToCart(data.id, data.id, data.quantity || 1, null);
              }
            } catch (e) {
              // Ignore parsing errors
            }
          }
        }).catch(function() {});
      }
      
      return result;
    };
  }

  // Flush events before page unload
  function setupUnloadHandler() {
    window.addEventListener('beforeunload', flushEvents);
    window.addEventListener('pagehide', flushEvents);
    document.addEventListener('visibilitychange', function() {
      if (document.visibilityState === 'hidden') {
        flushEvents();
      }
    });
  }

  // Initialize tracker
  function init() {
    // Get or create visitor
    getOrCreateVisitorKey();
    
    // Start/resume session
    getOrCreateSession();
    
    // Track initial page view
    trackPageView();
    
    // Set up add-to-cart interception
    interceptAddToCart();
    
    // Set up unload handlers
    setupUnloadHandler();

    console.log('[BarknBig] Tracker v' + TRACKER_VERSION + ' initialized');
  }

  // Expose API for manual tracking
  window.BarknBig = {
    trackEvent: queueEvent,
    trackAddToCart: trackAddToCart,
    trackPageView: trackPageView,
    getVisitorKey: getOrCreateVisitorKey,
    getSessionKey: function() { return getOrCreateSession().sessionKey; },
    flush: flushEvents
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();